import React, { useState, useContext, useCallback, useMemo } from "react";
import { LGrid, LButton, LNotificationGroup, LNotification, LFade } from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faEdit, faPlayCircle } from "@fortawesome/free-solid-svg-icons";
import styles from "./AutoImportConfigurations.module.css";
import { ConfirmationModal } from "../../TemplateManagement/TemplateManagementModals/SharedModals/ConfirmationModal";
import { AuthContext } from "../../../../../../context/AuthContext";
import { AutoImportFormData, AutoImportFormProvider } from "./Context/AutoImportFormContext";
import { AddEditAutoImportConfigurationsModal } from "./AddEditAutoImportModal/AddEditAutoImportConfigurationsModal";
import { ScheduledJob } from "./AddEditAutoImportModal/types/autoImportConfigContextTypes";
import { ProductType } from "@appTypes/productType";
import { AUTO_IMPORT_PRODUCTS } from "../constants/validAutoImportProducts";
import { useClientsConfig } from "../hooks/useClientsConfig";
import { useAutoImportConfigs } from "../hooks/useAutoImportConfigs";
import { CONNECTION_TYPES_DATA, CONNECTION_TYPES_ENUM } from "../constants/autoImportConnectionTypes";
import LoadingOverlay from "src/components/Shared/LoadingOverlay/LoadingOverlay";
import { SCHEDULED_RUN_TIMES_DATA } from "../constants/scheduledRunTimesTypes";
const GATEWAY_BASE_URL = process.env.REACT_APP_API_GATEWAY_BASE;

export interface AutoImportConfigurationData extends AutoImportFormData {
  scheduledJobGuid?: string | number;
}
interface GridCellProps {
  dataItem: ScheduledJob;
}
interface Column {
  field: string;
  title: string;
  width: number;
  cell?: (props: GridCellProps) => React.ReactNode;
  filterable?: boolean;
}

function AutoImportConfigurations() {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<AutoImportConfigurationData | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<ScheduledJob|null>(null);
  const [runNowSuccess, setRunNowSuccess] = useState(false);
  const [isRunConfirmOpen, setIsRunConfirmOpen] = useState(false);
  const [itemToRunConfirm, setItemToRunConfirm] = useState<ScheduledJob|null>(null);
  const authContext = useContext(AuthContext);
  const token: string = authContext.authState().accessToken.accessToken;
  const { clients } = useClientsConfig(token);
  const { data, loading, error, refetchConfigs  } = useAutoImportConfigs(token);

  const handleDeleteClick = useCallback((item:ScheduledJob) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!itemToDelete) return;
    try {
      const response = await fetch(
        `${GATEWAY_BASE_URL}/datamanager/api/Automation/${itemToDelete.scheduledJobGuid}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to delete auto import configuration.");
      refetchConfigs();
    } catch (err: any) {
      console.error("Error deleting configuration: ", err);
    } finally {
      setItemToDelete(null);
      setIsDeleteModalOpen(false);
    }
  }, [itemToDelete, refetchConfigs, token]);

  const handleAddButtonClick = useCallback(() => {
    setSelectedItem(null);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    refetchConfigs();
  }, [refetchConfigs]);

  const onSave = useCallback(() => {
    setIsModalOpen(false);
    refetchConfigs();
  }, [refetchConfigs]);

  const handleRunNowClick = useCallback((item: ScheduledJob) => {
    setItemToRunConfirm(item);
    setIsRunConfirmOpen(true);
  }, []);

  const handleRunNow = useCallback(
    async (item: ScheduledJob) => {
      const runTime = new Date(Date.now() - 2*24*60*60*1000).toISOString();
      try {
        const res = await fetch(
          `${GATEWAY_BASE_URL}/datamanager/api/Automation/${item.scheduledJobGuid}/LastRunDate`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(runTime),
          }
        );
        if (!res.ok) throw new Error("Failed to update lastRunDate");
        setRunNowSuccess(true);
        refetchConfigs();
      } catch (err) {
        console.error("Run Now error:", err);
      }
    },
    [token, refetchConfigs]
  );

  const handleConfirmRun = useCallback(async () => {
    if (!itemToRunConfirm) return;
    await handleRunNow(itemToRunConfirm);
    setIsRunConfirmOpen(false);
    setItemToRunConfirm(null);
  }, [itemToRunConfirm, handleRunNow]);


  const handleEditClick = useCallback(
    async (item: ScheduledJob) => {
      const res = await fetch(
        `${GATEWAY_BASE_URL}/multitenancy/api/Products/${item.productGuid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const product: ProductType = await res.json();

      const itemToEdit: AutoImportConfigurationData = {
        isActive: item.isActive,
        scheduledJobGuid: item.scheduledJobGuid,
        scheduledTime: item.scheduledTime,
        scheduleType: item.scheduleType,
        archiveAfterProcessing: item.archiveAfterProcessing,
        archivePath: item.archivePath,
        client: clients.find((c) => c.clientValuePayload.clientGuid === item.clientGuid),
        forDtc: item.forDTC,
        associatedInst: item.associatedInst,
        productType: product,
        clientProductGuid: item.clientProductGuid,
        jobName: item.jobName,
        ftpServer: item.ftpServer,
        sftpEnabled: item.sftpEnabled,
        ftpPort: item.ftpPort,
        ftpUsername: item.ftpUserName,
        ftpPassword: item.ftpPassword,
        expectedFileName: item.expectedFileName,
        fileHasHeaderRow: item.fileHasHeaderRow,
        sourceFileType: item.sourceFileType,
        blobStorageFolder: item.blobStorageFolder,
        blobStorageUsername: item.blobStorageUsername,
        blobStoragePassword: item.blobStoragePassword
      };
      setSelectedItem(itemToEdit);
      setIsModalOpen(true);
    },
    [clients, token]
  );

  const columns: Column[] = useMemo(() => {
    return [
      {
        field: "actions",
        title: "Actions",
        width: 100,
        cell: (props: GridCellProps) => (
          <td className={styles.actionButtons}>
            <button
              className={styles.editButton}
              onClick={() => handleEditClick(props.dataItem)}
              title="Edit"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
              className={styles.runNowButton}
              onClick={() => handleRunNowClick(props.dataItem)}
              title="Run Job Now"
            >
                <FontAwesomeIcon icon={faPlayCircle}/>
            </button>
            <button
              className={styles.deleteButton}
              onClick={() => handleDeleteClick(props.dataItem)}
              title="Delete"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </td>
        ),
        filterable: false,
      },
      {
        field: "isActive",
        title: "Status",
        width: 100,
        cell: (props: GridCellProps) =>
          props.dataItem.isActive ? (
            <td style={{ color: "green" }}>Active</td>
          ) : (
            <td style={{ color: "red" }}>Inactive</td>
          ),
        filterable: false,
      },
      {
        field: "jobName",
        title: "Job Name",
        width: 200,
        cell: (props: GridCellProps) => <td>{props.dataItem.jobName}</td>,
      },
      {
        field: "clientGuid",
        title: "Client",
        width: 250,
        cell: (props: GridCellProps) => {
          const clientName: string =
            clients.find(
              (e) => e.clientValuePayload.clientGuid === props.dataItem.clientGuid
            )?.formattedName ?? "";
          return <td>{clientName}</td>;
        },
      },
      {
        field: "productTypeGuid",
        title: "Product",
        width: 150,
        cell: (props: GridCellProps) => {
          const productName: string =
            AUTO_IMPORT_PRODUCTS.find(
              (e) =>
                e.productTypeGuid.toLowerCase() ===
                props.dataItem.productTypeGuid.toLowerCase()
            )?.productName ?? "";
          return <td>{productName}</td>;
        },
      },
      {
        field: "connectionType",
        title: "Connection Type",
        width: 150,
        cell: ({ dataItem }) => {
          const entry = CONNECTION_TYPES_DATA.find(
            d => d.value === dataItem.sourceFileType
          );
          return <td>{entry?.text ?? "-"}</td>;
        },
      },
      {
        field: "ftpServer",
        title: "FTP Server",
        width: 200,
        cell: ({ dataItem }) => {
          if (dataItem.sourceFileType === CONNECTION_TYPES_ENUM.FTP) {
            const server = dataItem.ftpServer || "-";
            const port = dataItem.ftpPort != null ? dataItem.ftpPort : "-";
            return <td>{`${server}:${port}`}</td>;
          }
          return <td>-</td>;
        },
      },
      {
        field: "blobFolder",
        title: "Blob Folder",
        width: 200,
        cell: ({ dataItem }) => (
          <td>
            {dataItem.sourceFileType === CONNECTION_TYPES_ENUM.BLOB
              ? dataItem.blobStorageFolder || "-"
              : "-"}
          </td>
        ),
      },
      {
        field: "scheduleType",
        title: "Run Schedule",
        width: 150,
        cell: ({ dataItem }) => {
          const entry = SCHEDULED_RUN_TIMES_DATA.find(
            d => d.value === dataItem.scheduleType
          );
          return <td>{entry?.text ?? "-"}</td>;
        },
      },
    ];
  }, [clients, handleEditClick, handleDeleteClick]);

  const loadingPanel = <LoadingOverlay loadingText="Loading"/>

  return (
    <div className={styles.AutoImportConfigurationsContainer}>
      <div className={styles.header}>
        <h2>Auto Import Configurations</h2>
        <LButton
          onClick={handleAddButtonClick}
          themeColor="primary"
          className="green"
        >
          <FontAwesomeIcon icon={faPlus} className={styles.buttonIcon} />
          Add Configuration
        </LButton>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {loading && loadingPanel}
      <LGrid
        data={data}
        columns={columns}
        loading={loading}
        sortable
        filterable
        resizable
        pageable
        style={{ height: "400px" }}
        className={styles.grid}
      />
      <AutoImportFormProvider>
        <AddEditAutoImportConfigurationsModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={onSave}
          initialData={selectedItem}
        />
      </AutoImportFormProvider>

      {/* confirmation modals  */}
      {/* confirmation for Delete */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Configuration"
        subtitle="Confirm Delete"
        message={
          <span>
            Are you sure you want to delete <strong>{itemToDelete?.jobName}</strong>? This action cannot be undone.
          </span>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {/* Confirmation for RUN NOW */}
      <ConfirmationModal
        isOpen={isRunConfirmOpen}
        onClose={() => setIsRunConfirmOpen(false)}
        onConfirm={handleConfirmRun}
        title="Run Job Now"
        subtitle="Confirm Run"
        message={
          <span>
            Are you sure you want to run <strong>{itemToRunConfirm?.jobName}</strong> now?
          </span>
        }
        confirmLabel="Run"
        cancelLabel="Cancel"
      />

      <LNotificationGroup>
        <LFade>
          {runNowSuccess && (
            <LNotification
              type={{ style: "success", icon: true }}
              closable
              onClose={() => setRunNowSuccess(false)}
            >
              <span>Run Now registered successfully.</span>
            </LNotification>
          )}
        </LFade>
      </LNotificationGroup>
    </div>
  );
}

export { AutoImportConfigurations };